// Copyright © 2024 Broadcom Inc. and its subsidiaries. All Rights Reserved.
import React, { useState, useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import keys from 'lodash/keys';
import isEqual from 'lodash/isEqual';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { makeStyles, Button } from '@material-ui/core';

import { getI18n } from "../../../utils/intl";
import { getCustomChartData } from '../../../reducers/metrics';
import { MetricFilterDropdownValues } from '../../../constants/customReport';
import { buildQuery, buildSaveQuery } from '../utils/customMetrics';

import Loading from "../../Loading";
import AddPanelModal from '../AddPanelModal';
import ChartPanel from '../ChartPanel';
import Footer from '../../Footer';

import styles from './styles';

const metricBasedyLabels = {
  hits: 'label.analytics.api.hits.instances.text',
  latency: 'label.analytics.customReport.api.latency.ms.text',
};

const dimensionMap = {
  apiId: 'api',
  apiKeyId: 'apiKey',
  appId: 'app',
  orgId: 'org',
  gatewayServerId: 'gatewayServer',
  uri: 'uri',
  respCode: 'respCode',
};

const chartTitleMap = {
  'API Hits': 'label.analytics.customReport.apiTrafficChart.title.text',
  'Average Response Time': 'label.analytics.customReport.latency.chart.title.text',
}

const chartDescriptionMap = {
  'API Hits': 'label.analytics.customReport.trafficChart.placeholder.title.text',
  'Average Response Time': 'label.analytics.customReport.latencyChart.placeholder.title.text',
}

const CustomReport = (props) => {
  const {
    totalPanels, apiList, appList, orgList, gatewayList, fetchCustomChartData,
    apiKeyDetailsMap, fetchChartPanels, fetchCustomReports, customReportPanels,
    customReports, saveCustomReport, customReportState, saveChartPanel,
    editChartPanel, isExternalUser, isOrgPublisher, handleSearchFilterNameChange,
    onEntityTypeChange,
  } = props;

  const intl = getI18n(useIntl());
  const useStyles = makeStyles(styles);
  const classes = useStyles();

  const [state, updateState] = useState({
    showAddPanelModal: false,
    chartPanels: [],
    editChartPanel: false,
    editChartPanelId: null,
    chartPanelData: [],
  });

  const setState = (value) => {
    if (typeof value === 'function') {
      updateState(currentValue => ({ ...currentValue, ...value(state) }));
    } else {
      updateState(currentValue => ({ ...currentValue, ...value }));
    }
  }

  useEffect(() => {
    fetchCustomReports().then(data => {
      const reportId = data && data.payload.length && data.payload[0].id;
      const defaultReportDetails =
        data && data.payload.length && data.payload[0];
      const isPanelLoading = customReportPanels && customReportPanels.loading;
      if (reportId && !isPanelLoading) {
        fetchChartPanels(reportId).then(chartPanel => {
          const chartPanels =
            chartPanel &&
            chartPanel.payload.length &&
            chartPanel.payload.map(chartData =>
              buildChartPanel(chartData),
            );
          if (chartPanels && chartPanels.length) {
            setState({
              chartPanels,
              defaultReportDetails,
            });
          }
        });
      }
    });
  }, []);

  const customReportsRef = useRef(customReports);
  useEffect(() => {
    if (
      keys(customReports).length
      && !customReports.loading
      && !isEqual(customReports.data, customReportsRef.current.data)
    ) {
      const defaultReportDetails = customReports.data.length ?
        customReports.data[0] : { name: 'Custom Report', description: '' };
      setState({ defaultReportDetails });
      if (!customReports.data.length) {
        saveCustomReport(defaultReportDetails).then(() => {
          fetchCustomReports();
        });
      } else {
        fetchChartPanels(defaultReportDetails.id);
      }
    }
    customReportsRef.current = customReports;
  }, [customReports]);

  const customReportPanelsRef = useRef(customReportPanels);
  useEffect(() => {
    if (
      keys(customReportPanels).length
      && !customReportPanels.loading
      && !isEqual(customReportPanels.data, customReportPanelsRef.current.data)
    ) {
      const chartPanels = customReportPanels.data.map(data =>
        buildChartPanel(data),
      );
      setState({
        chartPanelData: customReportPanels.data,
        chartPanels,
      });
    }
    customReportPanelsRef.current = customReportPanels;
  }, [customReportPanels]);

  const buildChartPanel = data => {
    const queryForChart = buildQuery(JSON.parse(JSON.stringify(data)));
    fetchCustomChartData(queryForChart, data.id);
    const selectedDataSource = [];
    if (data && data.dimensions &&
      !_.isUndefined(data.dimensions.dimensionSelectionOrder)
      && data.dimensions.dimensionSelectionOrder.length) {
      data.dimensions.dimensionSelectionOrder.forEach(order => {
        const filterData =
          data.dimensions.filter &&
          data.dimensions.filter.fields &&
          data.dimensions.filter.fields.find(
            filter => dimensionMap[filter.dimension] === order,
          );
        const groupByData =
          data.dimensions.groupBy.length &&
          data.dimensions.groupBy.find(
            goupByData => dimensionMap[goupByData.dimension] === order,
          );
        if (filterData) {
          if (filterData.type === 'regex' && filterData.dimension === 'uri') {
            selectedDataSource.push(filterData);
          } else {
            const values =
              filterData.values &&
              filterData.values.reduce((acc, elem) => {
                acc[elem] = true;
                return acc;
              }, {});
            const updatedFilterData = { ...filterData, values };
            selectedDataSource.push(updatedFilterData);
          }
        }
        if (groupByData) {
          selectedDataSource.push(groupByData);
        }
      });
    }
    return {
      title: data.name,
      description: data.description,
      id: data.id,
      selectedMetric: MetricFilterDropdownValues.value.find(
        val => val.value === data.metrics.type,
      ),
      yLabel: intl.getI18nMessage(
        metricBasedyLabels[data.metrics.type],
      ),
      selectedDataSource,
      dimensionOrder: data.dimensions.dimensionSelectionOrder,
      timeRange: data.timeRange,
    };
  };

  const addPanel = () => {
    setState({
      showAddPanelModal: true,
      editChartPanel: false,
    });
  };
  
  const handleExport = (e) => {
    window.print();
    e.preventDefault();
    e.target.blur();
    return false;
  };

  const closeModal = () => {
    setState({
      showAddPanelModal: false,
    });
  }

  const createChart = (
    selectedMetric,
    selectedId,
    selectedFilters,
    dimensionOrder,
    isChartLoadRequired) => {
    let newState = {};
    const chartID = selectedId;
    let panelData = {};
    let query = {};
    setState(prevState => {
      if (!selectedId) {
        panelData = {
          title: intl.getI18nMessage(
            chartTitleMap[selectedMetric.text],
          ),
          description: intl.getI18nMessage(
            chartDescriptionMap[selectedMetric.text],
          ),
          yLabel: intl.getI18nMessage(
            metricBasedyLabels[selectedMetric.value],
          ),
          selectedMetric,
          selectedDataSource: selectedFilters,
          dimensionOrder,
        };
        newState = {
          chartPanels: [
            ...prevState.chartPanels,
            panelData,
          ],
          showAddPanelModal: false,
        };
        if (isChartLoadRequired) {
          query = buildSaveQuery(panelData);
          saveChartPanel(query, state.defaultReportDetails.id)
            .then(() => { fetchChartPanels(state.defaultReportDetails.id); });
        }
      } else {
        const selectedPanelIndex =
          _.findIndex(prevState.chartPanels, panel => panel.id === selectedId);
        const selectedPanel = prevState.chartPanels[selectedPanelIndex];
        if (selectedPanel.selectedMetric.value !== selectedMetric.value) {
          selectedPanel.title = intl.getI18nMessage(
            chartTitleMap[selectedMetric.text],
          );
          selectedPanel.description = intl.getI18nMessage(
            chartDescriptionMap[selectedMetric.text],
          );
        }
        selectedPanel.yLabel = intl.getI18nMessage(
          metricBasedyLabels[selectedMetric.value],
        );
        selectedPanel.dimensionOrder = dimensionOrder;
        selectedPanel.selectedMetric = selectedMetric;
        selectedPanel.selectedDataSource = selectedFilters || ['All Data'];
        panelData = JSON.parse(JSON.stringify(selectedPanel));
        prevState.chartPanels[selectedPanelIndex] = selectedPanel;
        newState = {
          chartPanels: prevState.chartPanels,
          showAddPanelModal: false,
        };
        if (isChartLoadRequired) {
          query = buildSaveQuery(panelData);
          editChartPanel(query, state.defaultReportDetails.id, selectedId);
        }
      }
      if (isChartLoadRequired) {
        const queryForChart = buildQuery(query);
        fetchCustomChartData(
          queryForChart,
          chartID);
      }
      return { ...prevState, ...newState };
    });
  }

  const editChartTitleDesc = (titleDescObj, panelId) => {
    const selectedPanel = state.chartPanels.find(panel => panel.id === panelId);
    const panelData = {
      ...selectedPanel,
      title: titleDescObj.title.trim(),
      description: titleDescObj.description.trim(),
    };
    const editQuery = buildSaveQuery(panelData);
    editChartPanel(editQuery,
      state.defaultReportDetails.id, panelId).then(() => {
        setState(prevState => {
          const chartPanels = JSON.parse(JSON.stringify(prevState.chartPanels));
          const index = state.chartPanels.findIndex(panel => panel.id === panelId);
          chartPanels[index].title = titleDescObj.title.trim();
          chartPanels[index].description = titleDescObj.description.trim();
          return {
            chartPanels,
          };
        });
      });
  }

  const updateDateFilters = (timeRange, panelId) => {
    const selectedPanel = state.chartPanels.find(
      panel => panel.id === panelId,
    );
    selectedPanel.timeRange = timeRange;
    const editQuery = buildSaveQuery(selectedPanel, timeRange);
    editChartPanel(editQuery, state.defaultReportDetails.id, panelId).then(() => {
        fetchCustomChartData(
          buildQuery(editQuery),
          panelId,
        );
      });
  };

  const openAddPanelModal = (id, dimensionId, selectedDataSource) => {
    setState({
      showAddPanelModal: true,
      editChartPanel: true,
      editChartPanelId: id,
      selectedPanelDataSource: selectedDataSource,
      selectedDimensionId: dimensionId,
    });
  }

  const renderEmptyPanels = (chartPanels, totalPanels, classes) => {
    const remainingSlots = totalPanels - chartPanels.length;
    const numberOfEmptyPanels = remainingSlots > 0 ? remainingSlots - 1 : 0;
    const emptyPanels = [];
    for (let i = 0; i < numberOfEmptyPanels; i++) {
      emptyPanels.push(
        <div key={i} data-apim-test={'empty-panels'} className={classes.chartPanel} />,
      );
    }
    return emptyPanels;
  };

  const entitiesMap = {
    apiMap: apiList,
    apiKeyMap: apiKeyDetailsMap,
    appMap: appList,
    orgMap: orgList,
    gatewayServerMap: gatewayList,
  };

  const panelDetail = state.chartPanels.length &&
    state.chartPanels.find(
      panel => panel.id === state.editChartPanelId,
    );

  return (
    <div>
      <div className={classes.dashboardContainer}>
        <div className={classes.dashboard}>
          <div className={classes.dashboardHeader}>
            <div className={classes.dashboardLabel}>
              {state.defaultReportDetails && state.defaultReportDetails.name}
              <div className={classes.placeholderText} />
            </div>
            <div className={classes.exportBtnContainer}>
              <Button className={classes.exportBtn} onClick={handleExport} >
                {intl.getI18nMessage('label.analytics.print.button.export')}
              </Button>
            </div>
          </div>
          <div className={classes.chartPanels}>
            {!keys(customReportPanels).length &&
              customReportPanels?.loading && (
                <div className={classes.loader}>
                  <Loading />
                </div>
              )
            }
            {state.chartPanels.length
              ? state.chartPanels.map(panel => (
                <ChartPanel
                  key={panel.id}
                  id={panel.id}
                  title={panel.title}
                  description={panel.description}
                  yLabel={panel.yLabel}
                  selectedMetric={panel.selectedMetric}
                  selectedDataSource={panel.selectedDataSource}
                  timeRange={panel.timeRange}
                  openAddPanelModal={openAddPanelModal}
                  chartResp={getCustomChartData(customReportState, panel.id)}
                  editChartTitleDesc={editChartTitleDesc}
                  updateDateFilters={updateDateFilters}
                  entitiesMap={entitiesMap}
                  apiList={apiList}
                  apiKeyDetailsMap={apiKeyDetailsMap}
                  appList={appList}
                  orgList={orgList}
                  gatewayList={gatewayList}
                />
              ))
              : null}
            {keys(customReportPanels).length &&
              !customReportPanels.loading &&
              state.chartPanels.length < totalPanels ? (
                <div className={classes.chartPanel}>
                  <Button
                    data-apim-test={'add-panel-button'}
                    className={classes.addPanelBtn}
                    onClick={addPanel}
                  >
                    {intl.getI18nMessage('label.analytics.customReport.dashboard.button.createChart.label')}
                  </Button>
                </div>
              ) : null}
            {keys(customReportPanels).length &&
              !customReportPanels.loading ?
              renderEmptyPanels(state.chartPanels, totalPanels, classes) : null}
            {state.showAddPanelModal && !state.editChartPanel && (
              <AddPanelModal
                closeModal={closeModal}
                createChart={createChart}
                apiList={apiList}
                apiKeyDetailsMap={apiKeyDetailsMap}
                appList={appList}
                orgList={orgList}
                gatewayList={gatewayList}
                isExternalUser={isExternalUser}
                isOrgPublisher={isOrgPublisher}
                handleSearchFilterNameChange={handleSearchFilterNameChange}
                onEntityTypeChange={onEntityTypeChange}
              />
            )}
            {state.showAddPanelModal && state.editChartPanel
              && <AddPanelModal
                selectedMetric={panelDetail && panelDetail.selectedMetric}
                selectedDimension={state.selectedDimensionId}
                editPanelId={panelDetail && panelDetail.id}
                selectedPanelDataSource={state.selectedPanelDataSource}
                closeModal={closeModal}
                apiList={apiList}
                apiKeyDetailsMap={apiKeyDetailsMap}
                appList={appList}
                orgList={orgList}
                gatewayList={gatewayList}
                createChart={createChart}
                isExternalUser={isExternalUser}
                isOrgPublisher={isOrgPublisher}
                handleSearchFilterNameChange={handleSearchFilterNameChange}
                onEntityTypeChange={onEntityTypeChange}
              />
            }
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}

CustomReport.propTypes = {
  totalPanels: PropTypes.number,
  customReportState: PropTypes.object,
  fetchCustomChartData: PropTypes.func,
  apiList: PropTypes.object,
  apiKeyDetailsMap: PropTypes.object,
  appList: PropTypes.object,
  orgList: PropTypes.object,
  isExternalUser: PropTypes.bool,
  isOrgPublisher: PropTypes.bool,
  gatewayList: PropTypes.object,
  saveCustomReport: PropTypes.func,
  customReports: PropTypes.object,
  saveChartPanel: PropTypes.func,
  fetchChartPanels: PropTypes.func,
  customReportPanels: PropTypes.object,
  editChartPanel: PropTypes.func,
  fetchCustomReports: PropTypes.func,
  handleSearchFilterNameChange: PropTypes.func,
  onEntityTypeChange: PropTypes.func,
};

export default CustomReport;
